import type { FC } from 'react';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import LayoutContainer from '../../_partials/LayoutContainer';
import StudioHeaderMobile from '../../../StudioHeaderMobile';
import ProductsHeader from '../../../products/ProductsHeader';
import { useDraftsSelect } from '../../../../hooks/useDraftsSelect';
import { STUDIO_DEVIATIONS_STREAM } from '../../../../../constants';
import { StudioSection } from '../../../../types/store';
import StudioBlankSlate from '../../../StudioBlankSlate';
import StudioTableBatchBanner from '../../../StudioTableBatchBanner';
import ProductList from './ProductList';

interface Props {
  activeSection: StudioSection;
}

const ProductsLayout: FC<Props> = ({ activeSection }) => {
  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const {
    items: deviations,
    hasMore,
    hasLess,
  } = useNormalizedDeviationStream(streamId, streamThrottle, {
    ...streamConfig,
  });

  const { selectedCount, hasSelection, allIsSelected, onSelectAll } =
    useDraftsSelect(deviations);

  const showStudioHeader =
    activeSection === StudioSection.PRODUCTS_LOBBY || hasSelection;
  const showProductsHeader = !hasSelection;

  // we have a special case on commissions where we show the blank slate when all commissions are closed
  // for now we don't have a backend flag for this so we do it when there's a single results page
  const isCommissionsSection =
    activeSection === StudioSection.PRODUCTS_COMMISSION;
  const isSinglePage = !hasMore && !hasLess;
  const allCommissionsClosed =
    isCommissionsSection &&
    isSinglePage &&
    deviations.every(deviation => deviation.commission?.status === -1);
  const hasItems = deviations.length > 0;
  const showBlankSlate = !hasItems || allCommissionsClosed;

  return (
    <LayoutContainer>
      <StudioTableBatchBanner />
      {showStudioHeader && (
        <StudioHeaderMobile
          selectedCount={selectedCount}
          allIsSelected={allIsSelected}
          onSelectAll={onSelectAll}
        />
      )}
      {showProductsHeader && <ProductsHeader />}
      {showBlankSlate ? <StudioBlankSlate /> : <ProductList />}
    </LayoutContainer>
  );
};
ProductsLayout.displayName = 'ProductsLayout';

export default ProductsLayout;
